import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from 'antd';
import { SelectOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { useMutation, useQuery } from '@apollo/client/react';

import DisplayValue from 'components/DisplayValue/DisplayValue';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import SimpleGrid from 'components/SimpleGrid/SimpleGrid';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import { CUSTOMER_BANKRUPTCY_LEGAL_INFO } from 'queries/bankruptcy';
import { UPDATE_BANKRUPTCY_LEGAL_INFO } from 'mutations/bankruptcy';
import useTimeoutValue from 'hooks/useTimeoutValue';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  BANKRUPTCY_CHAPTER,
  BANKRUPTCY_FILING_STAGE,
  STATUS,
  SIZE,
} from 'utilities/constants';
import { getCardDisplayName } from 'utilities/helpers';
import { formatDate, formatDateToSubmit } from 'utilities/datesAndTimes';

const DISMISSAL_DISCHARGE_OPTIONS = [
  {
    label: 'Dismissal',
    value: BANKRUPTCY_FILING_STAGE.DISMISSAL,
  },
  {
    label: 'Discharge',
    value: BANKRUPTCY_FILING_STAGE.DISCHARGE,
  },
];

const CHAPTER_OPTIONS = [
  { label: 'Chapter 7', value: BANKRUPTCY_CHAPTER.CHAPTER_7 },
  { label: 'Chapter 11', value: BANKRUPTCY_CHAPTER.CHAPTER_11 },
  { label: 'Chapter 12', value: BANKRUPTCY_CHAPTER.CHAPTER_12 },
  { label: 'Chapter 13', value: BANKRUPTCY_CHAPTER.CHAPTER_13 },
  { label: 'Chapter 128', value: BANKRUPTCY_CHAPTER.CHAPTER_128 },
];

const ACCOUNT_ID = 'accountId';
const CASE_NUMBER = 'caseNumber';
const CHAPTER = 'chapter';
const DATE_FILED = 'dateFiled';
const STAGE = 'stage';
const WITHDRAWN_DISMISSED_DATE = 'withdrawnDismissedDate';

const BankruptcyManagementModal = ({
  open,
  hide = () => {},
  ...modalProps
}) => {
  const { customerId, accountId: viewedAccountId } = useParams();
  const { customerInfo } = useCustomerInfo();
  const [bankruptcyForm] = Form.useForm();
  const selectedFilingStage = Form.useWatch(STAGE, bankruptcyForm);
  const selectedAccountId = Form.useWatch(ACCOUNT_ID, bankruptcyForm);
  const selectedDateFiled = Form.useWatch(DATE_FILED, bankruptcyForm);
  const [updateSuccess, setUpdateSuccess] = useTimeoutValue(false);
  const [partialSuccess, setPartialSuccess] = useState(false);
  const [failureMessages, setFailureMessages] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isRefiling, setIsRefiling] = useState(false);

  const bankruptcyLegalInfoQuery = useQuery(CUSTOMER_BANKRUPTCY_LEGAL_INFO, {
    variables: { customerId },
  });

  const activeAccountId = viewedAccountId || selectedAccountId;

  const { bankruptcyLegalInfo, accountOptions } = useMemo(() => {
    let bankruptcyLegalInfo;
    let accountOptions = [];
    if (bankruptcyLegalInfoQuery.data) {
      const accounts =
        bankruptcyLegalInfoQuery.data?.customer?.creditAccounts || [];
      const currentAccount = accounts.find(
        (account) => account.id === activeAccountId,
      );
      bankruptcyLegalInfo = currentAccount?.bankruptcyLegalInfo;

      accountOptions = accounts.map((account) => {
        return {
          value: account.id,
          label: getCardDisplayName(account),
        };
      });
    }

    return {
      bankruptcyLegalInfo,
      accountOptions,
    };
  }, [bankruptcyLegalInfoQuery.data, activeAccountId]);

  const [updateBankruptcyLegalInfo, updateBankruptcyLegalInfoMutation] =
    useMutation(UPDATE_BANKRUPTCY_LEGAL_INFO, {
      refetchQueries: ({ data }) => {
        const results = data?.updateBankruptcyLegalInfo;
        const queries = [];

        if (results?.legalInfoResult?.success) {
          queries.push('GetCustomerBankruptcyLegalInfo');
        }
        if (results?.autopayCancellationResult?.errors?.length === 0) {
          queries.push('GetAutopayStatus');
        }
        if (results?.ceaseAndDesistResult?.success) {
          queries.push('GetCustomerCommPreferences');
        }

        return queries;
      },
    });

  const submitting = updateBankruptcyLegalInfoMutation.loading;

  const isCurrentlyDismissalOrDischarge =
    bankruptcyLegalInfo?.stage === BANKRUPTCY_FILING_STAGE.DISMISSAL ||
    bankruptcyLegalInfo?.stage === BANKRUPTCY_FILING_STAGE.DISCHARGE;

  const reset = () => {
    setIsEditing(false);
    setIsRefiling(false);
    setFailureMessages([]);
    setPartialSuccess(false);
    bankruptcyForm.resetFields();
  };

  const closeModal = () => {
    reset();
    hide();
  };

  const handleSubmit = async () => {
    try {
      await bankruptcyForm.validateFields();
    } catch (error) {
      return;
    }

    setFailureMessages([]);
    setPartialSuccess(false);

    const values = bankruptcyForm.getFieldsValue();

    const stage = values.stage || BANKRUPTCY_FILING_STAGE.PETITION;

    const submittingDismissal = stage === BANKRUPTCY_FILING_STAGE.DISMISSAL;
    const withdrawnDismissedDate = submittingDismissal
      ? formatDateToSubmit(values.withdrawnDismissedDate)
      : undefined;

    const variables = {
      accountId: viewedAccountId || values.accountId,
      customerId,
      customerResidentialState:
        customerInfo?.customer?.contactInfo?.address?.state || '',
      stage,
      withdrawnDismissedDate,
      reset: !submittingDismissal,
      caseNumber: values.caseNumber || bankruptcyLegalInfo?.caseNumber,
      chapter: values.chapter || bankruptcyLegalInfo?.chapter,
      dateFiled: values.dateFiled
        ? formatDateToSubmit(values.dateFiled)
        : bankruptcyLegalInfo?.dateFiled,
    };

    try {
      const response = await updateBankruptcyLegalInfo({
        variables,
      });

      const {
        autopayCancellationResult,
        ceaseAndDesistResult,
        fundingAccountDisableResult,
        legalInfoResult,
        paymentCancellationResult,
        statementPreferencesResult,
        statusUpdateResults,
      } = response?.data?.updateBankruptcyLegalInfo || {};

      const legalInfoFailure = legalInfoResult && !legalInfoResult.success;

      if (legalInfoFailure) {
        setFailureMessages(['Failed to update bankruptcy legal info.']);
        return;
      }

      const autopayFailure = !!autopayCancellationResult?.errors?.length;
      const paymentFailure = !!paymentCancellationResult?.errors?.length;
      const fundingAccountFailure =
        !!fundingAccountDisableResult?.errors?.length;
      const statusUpdateFailure = !!statusUpdateResults?.errors?.length;

      const ceaseAndDesistFailure =
        ceaseAndDesistResult && !ceaseAndDesistResult.success;
      const statementPreferencesFailure =
        statementPreferencesResult && !statementPreferencesResult.success;

      setIsEditing(false);
      setIsRefiling(false);

      const failureMessages = [];

      if (autopayFailure) {
        failureMessages.push(
          'Failed to cancel autopay on for this account. Please handle this manually.',
        );
      }

      if (ceaseAndDesistFailure) {
        failureMessages.push(
          'Failed to apply Cease and Desist status. Please handle this manually.',
        );
      }

      if (paymentFailure) {
        failureMessages.push(
          'Failed to cancel all scheduled/pending payments. Please handle this manually.',
        );
      }

      if (statementPreferencesFailure) {
        failureMessages.push(
          'Failed to set paper statement preference on all accounts. Please handle this manually.',
        );
      }

      if (fundingAccountFailure) {
        failureMessages.push(
          'Failed to disable funding accounts. Please handle this manually.',
        );
      }

      if (statusUpdateFailure) {
        const failedStatuses = statusUpdateResults.errors
          .map((error) => `${error.operation}`)
          .join(', ');
        failureMessages.push(
          `Some status updates could not be applied: ${failedStatuses}. This may be expected if the account already has these statuses. Please verify.`,
        );
      }

      if (failureMessages.length) {
        setFailureMessages(failureMessages);
        setPartialSuccess(true);
        return;
      }

      setUpdateSuccess(true);
    } catch (error) {
      setFailureMessages(['Failed to submit update of bankruptcy legal info.']);
    }
  };

  const dismissalDateValidator = (_, value) => {
    const dateFiled = selectedDateFiled || bankruptcyLegalInfo.dateFiled;

    if (dateFiled && !dayjs(value).isAfter(dateFiled)) {
      return Promise.reject(
        new Error('Dismissal date must be after filing date'),
      );
    }
    return Promise.resolve();
  };

  const renderFullForm = () => {
    let formTitle = 'File a Petition for Bankruptcy';
    if (isEditing) {
      formTitle = isCurrentlyDismissalOrDischarge
        ? 'Edit Dismissal/Discharge Info'
        : 'Edit Petition Info';
    }

    return (
      <div style={{ position: 'relative' }}>
        <SectionMessage
          cover={true}
          icon={SelectOutlined}
          size={SIZE.XS}
          visible={!activeAccountId}
          body={
            <Typography.Paragraph type="secondary" align="center">
              Please select an account to manage
            </Typography.Paragraph>
          }
        />
        <Flex justify="space-between">
          <Typography.Text strong data-testid="form-title">
            {formTitle}
          </Typography.Text>
          {isEditing || isRefiling ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => {
                setIsEditing(false);
                setIsRefiling(false);
              }}
            >
              {isEditing ? 'Cancel Edit' : 'Cancel Petition'}
            </Button>
          ) : null}
        </Flex>
        <Divider style={{ marginTop: 'var(--spacing-xs)' }} />
        <SimpleGrid>
          <Form.Item
            required
            label="Filing Stage"
            name={STAGE}
            style={{ gridColumn: 'span 6' }}
          >
            {isCurrentlyDismissalOrDischarge && isEditing ? (
              <Select
                data-testid="filing-stage-select"
                options={DISMISSAL_DISCHARGE_OPTIONS}
              />
            ) : (
              <Select
                disabled
                data-testid="filing-stage-select"
                defaultValue={BANKRUPTCY_FILING_STAGE.PETITION}
                options={[
                  {
                    label: 'Petition',
                    value: BANKRUPTCY_FILING_STAGE.PETITION,
                  },
                ]}
              />
            )}
          </Form.Item>

          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please enter a case number.',
              },
            ]}
            label="Case Number"
            name={CASE_NUMBER}
            style={{ gridColumn: 'span 6' }}
          >
            <Input data-testid="case-number-input" />
          </Form.Item>
          <Form.Item
            label="Chapter"
            name={CHAPTER}
            style={{ gridColumn: 'span 6' }}
            rules={[
              {
                required: true,
                message: 'Please select a chapter.',
              },
            ]}
          >
            <Select data-testid="chapter-select" options={CHAPTER_OPTIONS} />
          </Form.Item>
          <Form.Item
            required
            label="Filing Date"
            name={DATE_FILED}
            style={{ gridColumn: 'span 6' }}
            rules={[
              {
                required: true,
                message: 'Please select a filing date.',
              },
            ]}
          >
            <DatePicker
              data-testid="filing-date-select"
              style={{ width: '100%' }}
            />
          </Form.Item>
          {selectedFilingStage === BANKRUPTCY_FILING_STAGE.DISMISSAL ? (
            <Form.Item
              required
              label="Dismissal Date"
              name={WITHDRAWN_DISMISSED_DATE}
              style={{ gridColumn: 'span 6' }}
              rules={[
                {
                  required: true,
                  message: 'Please select a dismissal date',
                },
                () => ({
                  validator: dismissalDateValidator,
                }),
              ]}
            >
              <DatePicker
                data-testid="dismissal-date-select"
                style={{ width: '100%' }}
              />
            </Form.Item>
          ) : undefined}
        </SimpleGrid>
      </div>
    );
  };

  const renderSecondaryActionForm = () => {
    const withdrawnDismissedDate = bankruptcyLegalInfo?.withdrawnDismissedDate
      ? formatDate(bankruptcyLegalInfo.withdrawnDismissedDate)
      : '-';
    return (
      <>
        <Flex justify="space-between">
          <Typography.Text strong>Current Filing Status</Typography.Text>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        </Flex>
        <Divider
          style={{
            marginTop: 'var(--spacing-xs)',
            marginBottom: 'var(--spacing-sm)',
          }}
        />
        <div
          style={{ marginBottom: 'var(--spacing-xl)' }}
          data-testid="bankruptcy-legal-info-summary"
        >
          <SimpleGrid
            style={{ gridGap: 'var(--spacing-sm) var(--spacing-sm)' }}
          >
            <DisplayValue label="Stage" style={{ gridColumn: 'span 2' }}>
              {bankruptcyLegalInfo.stage}
            </DisplayValue>

            <DisplayValue label="Case Number" style={{ gridColumn: 'span 4' }}>
              {bankruptcyLegalInfo.caseNumber}
            </DisplayValue>

            <DisplayValue label="Chapter" style={{ gridColumn: 'span 2' }}>
              {bankruptcyLegalInfo.chapter}
            </DisplayValue>

            <DisplayValue label="Filing Date" style={{ gridColumn: 'span 2' }}>
              {formatDate(bankruptcyLegalInfo.dateFiled)}
            </DisplayValue>

            <DisplayValue
              label="Dismissal Date"
              style={{ gridColumn: 'span 2' }}
            >
              {withdrawnDismissedDate}
            </DisplayValue>
          </SimpleGrid>
        </div>

        {isCurrentlyDismissalOrDischarge ? (
          <>
            <Typography.Text strong data-testid="form-title">
              Refile Petition
            </Typography.Text>
            <Divider
              style={{
                marginTop: 'var(--spacing-xs)',
                marginBottom: 'var(--spacing-sm)',
              }}
            />
            <Button
              type="primary"
              onClick={() => setIsRefiling(true)}
              data-testid="refiling-button"
            >
              File a new Petition for Bankruptcy
            </Button>
          </>
        ) : (
          <>
            <Typography.Text strong data-testid="form-title">
              Dismiss/Discharge
            </Typography.Text>
            <Divider
              style={{
                marginTop: 'var(--spacing-xs)',
                marginBottom: 'var(--spacing-sm)',
              }}
            />
            <SimpleGrid>
              <Form.Item
                required
                label="Filing Stage"
                name={STAGE}
                style={{ gridColumn: 'span 3' }}
                rules={[
                  {
                    required: true,
                    message: 'Please select a filing stage',
                  },
                ]}
              >
                <Select options={DISMISSAL_DISCHARGE_OPTIONS} />
              </Form.Item>
              {selectedFilingStage === BANKRUPTCY_FILING_STAGE.DISMISSAL ? (
                <Form.Item
                  required
                  label="Dismissal Date"
                  name={WITHDRAWN_DISMISSED_DATE}
                  style={{ gridColumn: 'span 3' }}
                  rules={[
                    {
                      required: true,
                      message: 'Please select a dismissal date',
                    },
                    () => ({
                      validator: dismissalDateValidator,
                    }),
                  ]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              ) : undefined}
            </SimpleGrid>
          </>
        )}
      </>
    );
  };

  const renderForm = () => {
    if (isEditing || isRefiling || !bankruptcyLegalInfo) {
      return renderFullForm();
    } else {
      return renderSecondaryActionForm();
    }
  };

  useEffect(
    function initializeFormValues() {
      if (isEditing) {
        bankruptcyForm.setFieldsValue({
          [CASE_NUMBER]: bankruptcyLegalInfo?.caseNumber,
          [CHAPTER]: bankruptcyLegalInfo?.chapter,
          [WITHDRAWN_DISMISSED_DATE]:
            bankruptcyLegalInfo?.withdrawnDismissedDate
              ? dayjs(bankruptcyLegalInfo.withdrawnDismissedDate)
              : undefined,
          [DATE_FILED]: bankruptcyLegalInfo?.dateFiled
            ? dayjs(bankruptcyLegalInfo.dateFiled)
            : undefined,
          [STAGE]: bankruptcyLegalInfo?.stage,
        });
      } else {
        bankruptcyForm.setFieldsValue({
          [CASE_NUMBER]: undefined,
          [CHAPTER]: undefined,
          [WITHDRAWN_DISMISSED_DATE]: undefined,
          [DATE_FILED]: undefined,
          [STAGE]: undefined,
        });
      }
    },
    [bankruptcyForm, bankruptcyLegalInfo, isEditing],
  );

  useEffect(() => {
    reset();
  }, [customerId, viewedAccountId]);

  return (
    <Modal
      {...modalProps}
      title="Manage Account Bankruptcy"
      width="600px"
      confirmLoading={submitting}
      open={open}
      onCancel={closeModal}
      onOk={handleSubmit}
      okText="Submit"
      okButtonProps={{
        hidden: isCurrentlyDismissalOrDischarge && !isRefiling && !isEditing,
        disabled: submitting,
      }}
    >
      <QueryBoundary
        query={bankruptcyLegalInfoQuery}
        mode="message"
        loadingMessage="Loading Bankruptcy Legal Info"
        loadingOverride={
          bankruptcyLegalInfoQuery.loading && !bankruptcyLegalInfo
        }
      >
        {updateSuccess && (
          <SectionMessage
            status={STATUS.SUCCESS}
            size={SIZE.XS}
            cover={true}
            text={`Bankruptcy status updated`}
          />
        )}

        <Form
          form={bankruptcyForm}
          layout="vertical"
          style={{ paddingTop: 'var(--spacing-md)' }}
          disabled={submitting}
        >
          {viewedAccountId ? undefined : (
            <Form.Item name={ACCOUNT_ID} label="Select an Account">
              <Select options={accountOptions} />
            </Form.Item>
          )}
          {renderForm()}
        </Form>

        {partialSuccess || failureMessages.length ? (
          <Flex
            gap="small"
            vertical={true}
            style={{ paddingTop: 'var(--spacing-sm)' }}
          >
            {partialSuccess && (
              <Alert
                type="success"
                title={'Successfully updated bankruptcy status'}
              />
            )}

            {failureMessages.map((message, index) => (
              <Alert
                type="error"
                title={message}
                key={`error-alert-${index}`}
              />
            ))}
          </Flex>
        ) : undefined}
      </QueryBoundary>
    </Modal>
  );
};

export default BankruptcyManagementModal;
